import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ConcentrationRisk,
  PortfolioRiskMap,
  RiskRating,
  RiskScore,
  RiskScorecard,
} from '@/types';

type PortfolioRiskMapResponse = {
  portfolioSize?: number;
  averageScore?: number;
  totalExposure?: number;
  riskWeightedExposure?: number;
  countByRating?: Partial<Record<RiskRating, number>>;
};

type ConcentrationRiskResponse = {
  topBorrowers?: Array<{
    borrowerId?: string;
    borrowerName?: string;
    exposure?: number;
    percentage?: number;
  }>;
  borrowerConcentration?: Array<{
    borrowerId?: string;
    borrowerName?: string;
    exposure?: number;
    percentage?: number;
  }>;
  sectorConcentration?: Array<{
    sector?: string;
    exposure?: number;
    percentage?: number;
  }>;
  geographicConcentration?: Array<{
    state?: string;
    exposure?: number;
    percentage?: number;
  }>;
};

const riskRatings: RiskRating[] = ['LOW', 'MODERATE', 'ELEVATED', 'HIGH', 'CRITICAL'];

function normalizePortfolioRiskMap(data: PortfolioRiskMapResponse): PortfolioRiskMap {
  return {
    byRating: riskRatings.reduce(
      (acc, rating) => {
        acc[rating] = {
          count: data.countByRating?.[rating] ?? 0,
          totalExposure: 0,
        };
        return acc;
      },
      {} as PortfolioRiskMap['byRating'],
    ),
    avgScore: data.averageScore ?? 0,
    totalLoansScored: data.portfolioSize ?? 0,
    riskWeightedExposure: data.riskWeightedExposure ?? 0,
  };
}

function normalizeConcentrationRisk(data: ConcentrationRiskResponse): ConcentrationRisk {
  const borrowerConcentration = (data.borrowerConcentration ?? data.topBorrowers ?? []).map(
    entry => ({
      borrowerName: entry.borrowerName ?? entry.borrowerId ?? 'Unknown borrower',
      exposure: entry.exposure ?? 0,
      percentage: entry.percentage ?? 0,
    }),
  );

  return {
    borrowerConcentration,
    sectorConcentration: (data.sectorConcentration ?? []).map(entry => ({
      sector: entry.sector ?? 'Unknown sector',
      exposure: entry.exposure ?? 0,
      percentage: entry.percentage ?? 0,
    })),
    geographicConcentration: (data.geographicConcentration ?? []).map(entry => ({
      state: entry.state ?? 'Unknown state',
      exposure: entry.exposure ?? 0,
      percentage: entry.percentage ?? 0,
    })),
  };
}

export function useRiskScore(loanId: string) {
  return useQuery({
    queryKey: ['risk-score', loanId],
    queryFn: () => api.get(`/risk/score/${loanId}`).then(r => r.data as RiskScore),
    enabled: !!loanId,
  });
}

export function useScoreLoan(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/risk/score/${loanId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['risk-score', loanId] }),
  });
}

export function useRiskScoreHistory(loanId: string) {
  return useQuery({
    queryKey: ['risk-score-history', loanId],
    queryFn: () => api.get(`/risk/score/${loanId}/history`).then(r => r.data as RiskScore[]),
    enabled: !!loanId,
  });
}

export function usePortfolioRiskMap() {
  return useQuery({
    queryKey: ['portfolio-risk-map'],
    queryFn: () =>
      api
        .get(`/risk/portfolio`)
        .then(r => normalizePortfolioRiskMap(r.data as PortfolioRiskMapResponse)),
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });
}

export function useConcentrationRisk() {
  return useQuery({
    queryKey: ['concentration-risk'],
    queryFn: () =>
      api
        .get(`/risk/concentration`)
        .then(r => normalizeConcentrationRisk(r.data as ConcentrationRiskResponse)),
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });
}

export function useRiskScorecards() {
  return useQuery({
    queryKey: ['risk-scorecards'],
    queryFn: () => api.get(`/risk/scorecards`).then(r => r.data as RiskScorecard[]),
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });
}
